import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Stethoscope } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminLogin, setToken, getToken } from "@/lib/api";
import { toast } from "sonner";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (getToken()) {
    // already logged in; bounce to dashboard
    setTimeout(() => navigate("/admin", { replace: true }), 0);
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await adminLogin(email.trim().toLowerCase(), password);
      setToken(data.access_token);
      toast.success(`Welcome back, ${data.user.name || "Dr. Delmar"} 💜`);
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper-50 px-5 py-12 paper-grain" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <span className="flex items-center justify-center w-9 h-9 rounded-sm bg-forest text-paper-50">
            <Stethoscope className="w-5 h-5" strokeWidth={2.2} />
          </span>
          <span className="font-display font-black text-lg tracking-tight text-ink leading-none">
            The STEM Vet<span className="text-terracotta">.</span>
          </span>
        </div>
        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="bg-paper-100 border border-paper-200 p-7 sm:p-9 rounded-sm space-y-5"
        >
          <div className="flex items-center gap-2 text-ink mb-2">
            <Lock className="w-4 h-4" />
            <h1 className="font-display font-bold text-2xl tracking-tight">Sign in to admin</h1>
          </div>
          <p className="text-sm text-ink-muted -mt-1">
            Manage your blog posts. Only you can access this.
          </p>

          <div>
            <Label htmlFor="email" className="text-ink font-semibold">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
              required
              autoComplete="username"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-ink font-semibold">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-forest text-paper-50 px-5 py-3.5 text-sm font-semibold rounded-sm hover:bg-forest-700 transition-colors disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>

          <p className="text-xs text-ink-muted text-center">
            Forgot your password? Just message Claude in the Emergent project.
          </p>
        </form>
      </div>
    </div>
  );
}
